/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
/*
  Accepts a value and timeout and will debounce
  
  @param {S} value: value of element
  @param {timeout} number: timer 
  @return {value: S}  

  @example: 
   const debouncedName = useDebounce('testing', 2000)
*/
export const useDebounce = <S>(value: S, timeout: number): S => {
  const [state, setState] = useState<S>(value);

  useEffect(() => {
    if (timeout <= 0) {
      return (): void => {};
    }
    const handler = setTimeout(() => {
      if (!isEqual(value, state)) {
        setState(value);
      }
    }, timeout);
    return (): void => clearTimeout(handler);
  }, [value, timeout, state]);

  return timeout <= 0 ? value : state;
};
